import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Inbox, CheckCircle, Clock, AlertCircle, ArrowRight } from '../icons';
import { supabase } from '../../lib/supabase';
import { ResultsGraph } from '../ResultsGraph';
import { HarmonicGraph } from '../HarmonicGraph';
import { AIAnalysisPanel } from '../ai/AIAnalysisPanel';
import { LoadingSpinner } from '../LoadingSpinner';
import { checkCoachAIAccess } from '../../lib/auth';
import { useAuth } from '../../contexts/AuthContext';
import type { AssessmentResult } from '../../types/results';

interface SharedResultItem {
  share_id: string;
  result_id: string;
  shared_by_name: string;
  shared_by_email: string;
  message: string | null;
  status: string;
  created_at: string;
  result: {
    id: string;
    user_id: string;
    scores: any;
    harmonic_scores: any;
    completed_at: string;
    coach_ai_analysis: string | null;
  };
}

interface SharedResultsInboxProps {
  recipientType: 'coach' | 'partner';
  recipientId: string;
}

export function SharedResultsInbox({ recipientType, recipientId }: SharedResultsInboxProps) {
  const { user } = useAuth();
  const [shares, setShares] = useState<SharedResultItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedShare, setSelectedShare] = useState<SharedResultItem | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [hasAIAccess, setHasAIAccess] = useState(false);
  const [aiAccessChecked, setAiAccessChecked] = useState(false);

  const loadShares = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .rpc('fetch_received_shares', {
          p_recipient_type: recipientType,
          p_recipient_id: recipientId,
        });

      if (err) throw err;

      const mapped = (data || []).map((row: any) => ({
        share_id: row.share_id,
        result_id: row.result_id,
        shared_by_name: row.sharer_name || 'Respondent',
        shared_by_email: row.sharer_email || '',
        message: row.message,
        status: row.status,
        created_at: row.created_at,
        result: {
          id: row.result_id,
          user_id: row.result_user_id,
          scores: row.result_scores,
          harmonic_scores: row.result_harmonic_scores,
          completed_at: row.result_completed_at,
          coach_ai_analysis: row.result_coach_ai_analysis || null,
        },
      })) as SharedResultItem[];

      setShares(mapped);
    } catch {
      setError('Unable to load shared results right now. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [recipientType, recipientId]);

  useEffect(() => {
    loadShares();
  }, [loadShares]);

  useEffect(() => {
    if (recipientType === 'coach' && user) {
      checkCoachAIAccess(user.id).then((hasAccess) => {
        setHasAIAccess(hasAccess);
        setAiAccessChecked(true);
      });
    } else {
      setAiAccessChecked(true);
    }
  }, [recipientType, user]);

  const handleAccept = async (shareId: string) => {
    setAccepting(true);
    await supabase.from('result_shares').update({ status: 'accepted' }).eq('id', shareId);
    setAccepting(false);
    setShares(prev => prev.map(s => s.share_id === shareId ? { ...s, status: 'accepted' } : s));
    setSelectedShare(prev => prev && prev.share_id === shareId ? { ...prev, status: 'accepted' } : prev);
  };

  const handleAnalysisUpdate = useCallback((analysis: string) => {
    setSelectedShare(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        result: { ...prev.result, coach_ai_analysis: analysis },
      };
    });
  }, []);

  if (loading) return <LoadingSpinner message="Loading shared results..." />;

  if (error) {
    return (
      <div className="freud-card-elevated p-8 text-center">
        <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <p className="text-chocolate-600">{error}</p>
      </div>
    );
  }

  if (shares.length === 0 && !selectedShare) {
    return (
      <div className="freud-card-elevated p-10 text-center">
        <div className="w-16 h-16 bg-cream-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Inbox className="w-8 h-8 text-chocolate-300" />
        </div>
        <h3 className="freud-heading-xl text-chocolate-900 mb-2">No Shared Results Yet</h3>
        <p className="text-chocolate-500 text-sm max-w-md mx-auto">
          When a respondent shares their assessment results with you, they'll appear here.
        </p>
      </div>
    );
  }

  if (selectedShare) {
    const result = selectedShare.result;
    const mapped: AssessmentResult = {
      id: result.id,
      userId: result.user_id,
      scores: result.scores || [],
      harmonicScores: result.harmonic_scores || [],
      completedAt: new Date(result.completed_at || Date.now()),
      version: 1,
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setSelectedShare(null)}
            className="flex items-center gap-2 text-chocolate-500 hover:text-sage-600 transition-colors text-sm font-medium"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Back to inbox
          </button>
          {selectedShare.status === 'pending' && (
            <button
              onClick={() => handleAccept(selectedShare.share_id)}
              disabled={accepting}
              className="freud-btn-primary inline-flex items-center gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              {accepting ? 'Accepting...' : 'Accept Share'}
            </button>
          )}
          {selectedShare.status === 'accepted' && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sage-100 text-sage-700 text-sm font-medium">
              <CheckCircle className="w-4 h-4" />
              Accepted
            </span>
          )}
        </div>

        <div className="freud-card-elevated p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center">
              <span className="text-sm font-bold text-sage-600">
                {selectedShare.shared_by_name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <p className="font-semibold text-chocolate-900">{selectedShare.shared_by_name}</p>
              <p className="text-xs text-chocolate-400">{selectedShare.shared_by_email}</p>
            </div>
          </div>
          {selectedShare.message && (
            <div className="mt-3 p-3 rounded-xl bg-cream-50 text-chocolate-600 text-sm italic">
              "{selectedShare.message}"
            </div>
          )}
          <p className="text-xs text-chocolate-400 mt-2">
            Shared on {new Date(selectedShare.created_at).toLocaleDateString()}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="freud-card-elevated p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center">
                <span className="text-sm font-bold text-sage-600">C</span>
              </div>
              <h2 className="freud-heading-xl text-chocolate-900">Characteristic Profile</h2>
            </div>
            <ResultsGraph scores={mapped.scores} />
          </div>
          <div className="freud-card-elevated p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-coral-100 rounded-xl flex items-center justify-center">
                <span className="text-sm font-bold text-coral-600">H</span>
              </div>
              <h2 className="freud-heading-xl text-chocolate-900">Harmonic Scale Profile</h2>
            </div>
            <HarmonicGraph scores={mapped.harmonicScores} />
          </div>
        </div>

        {recipientType === 'coach' && hasAIAccess && aiAccessChecked && (
          <AIAnalysisPanel
            analysis={selectedShare.result.coach_ai_analysis || undefined}
            scores={mapped.scores}
            harmonicScores={mapped.harmonicScores}
            resultId={selectedShare.result_id}
            showRegenerate
            respondentName={selectedShare.shared_by_name}
            storageField="coach_ai_analysis"
            onAnalysisUpdate={handleAnalysisUpdate}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="freud-heading-2xl text-chocolate-900">Shared Results Inbox</h2>
        <p className="text-chocolate-500 text-sm mt-1">
          Assessment results that respondents have shared with you.
        </p>
      </div>

      {shares.map((share, idx) => (
        <motion.div
          key={share.share_id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: idx * 0.05 }}
          className="freud-card-elevated p-5 hover:shadow-lg transition-all cursor-pointer"
          onClick={() => setSelectedShare(share)}
        >
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 bg-sage-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-bold text-sage-600">
                {share.shared_by_name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-chocolate-900 truncate">{share.shared_by_name}</p>
                {share.status === 'pending' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 text-[10px] font-bold uppercase tracking-wide">
                    <Clock className="w-3 h-3" />
                    Pending
                  </span>
                )}
                {share.status === 'accepted' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sage-50 text-sage-600 text-[10px] font-bold uppercase tracking-wide">
                    <CheckCircle className="w-3 h-3" />
                    Accepted
                  </span>
                )}
              </div>
              <p className="text-xs text-chocolate-400 truncate">
                {share.message ? `"${share.message}"` : `Completed on ${new Date(share.result?.completed_at || share.created_at).toLocaleDateString()}`}
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-xs text-chocolate-400">
                {new Date(share.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
}
